// Vertex-animated meshes (R_LoadMD3 in tr_model.c): per frame positions and
// normals, per frame tags. MDC (mdc.ts) loads into the same structure.

import { BinReader, surfaceName } from './bin';
import type { Axis, Orientation } from './skeleton';
import type { Vec3 } from '../../shared/math';

export interface MeshSurface {
  name: string;
  /** Shaders listed in the file (the first one is used when the skin has no entry). */
  shaders: string[];
  numVerts: number;
  indexes: Uint32Array;
  st: Float32Array;
  /** numFrames * numVerts * 3 */
  xyz: Float32Array;
  normals: Float32Array;
}

export interface MeshModel {
  name: string;
  numFrames: number;
  surfaces: MeshSurface[];
  /** Tag name -> per frame origin (3) and axis (9). */
  tags: Map<string, { origin: Float32Array; axis: Float32Array }>;
}

export const XYZ_SCALE = 1 / 64;

/** Normal from the MD3 lat/long encoding (tr.sinTable lookups). */
export function latLongNormal(n: number, out: Float32Array, o: number): void {
  const lat = (((n >> 8) & 0xff) * 2 * Math.PI) / 256;
  const lng = ((n & 0xff) * 2 * Math.PI) / 256;
  out[o] = Math.cos(lat) * Math.sin(lng);
  out[o + 1] = Math.sin(lat) * Math.sin(lng);
  out[o + 2] = Math.cos(lng);
}

export function readShaders(r: BinReader, o: number, n: number): string[] {
  const out: string[] = [];
  for (let i = 0; i < n; i++) out.push(r.str(o + i * 68));
  return out;
}

export function readTriangles(r: BinReader, o: number, n: number): Uint32Array {
  const out = new Uint32Array(n * 3);
  for (let i = 0; i < n * 3; i++) out[i] = r.i32(o + i * 4);
  return out;
}

export function readSt(r: BinReader, o: number, n: number): Float32Array {
  const out = new Float32Array(n * 2);
  for (let i = 0; i < n * 2; i++) out[i] = r.f32(o + i * 4);
  return out;
}

export function parseMd3(data: Uint8Array, name = 'md3'): MeshModel {
  const r = new BinReader(data);
  r.check('IDP3', [15], name);
  const numFrames = r.i32(76);
  const numTags = r.i32(80);
  const numSurfaces = r.i32(84);
  const ofsTags = r.i32(96);
  const ofsSurfaces = r.i32(100);

  const tags = new Map<string, { origin: Float32Array; axis: Float32Array }>();
  for (let t = 0; t < numTags; t++) {
    const origin = new Float32Array(numFrames * 3);
    const axis = new Float32Array(numFrames * 9);
    for (let f = 0; f < numFrames; f++) {
      const o = ofsTags + (f * numTags + t) * 112;
      for (let k = 0; k < 3; k++) origin[f * 3 + k] = r.f32(o + 64 + k * 4);
      for (let k = 0; k < 9; k++) axis[f * 9 + k] = r.f32(o + 76 + k * 4);
    }
    tags.set(r.str(ofsTags + t * 112).toLowerCase(), { origin, axis });
  }

  const surfaces: MeshSurface[] = [];
  let s = ofsSurfaces;
  for (let i = 0; i < numSurfaces; i++) {
    const numVerts = r.i32(s + 80);
    const xyz = new Float32Array(numFrames * numVerts * 3);
    const normals = new Float32Array(numFrames * numVerts * 3);
    const ofsXyz = s + r.i32(s + 100);
    for (let k = 0; k < numFrames * numVerts; k++) {
      const o = ofsXyz + k * 8;
      xyz[k * 3] = r.i16(o) * XYZ_SCALE;
      xyz[k * 3 + 1] = r.i16(o + 2) * XYZ_SCALE;
      xyz[k * 3 + 2] = r.i16(o + 4) * XYZ_SCALE;
      latLongNormal(r.i16(o + 6), normals, k * 3);
    }
    surfaces.push({
      name: surfaceName(r.str(s + 4)),
      shaders: readShaders(r, s + r.i32(s + 92), r.i32(s + 76)),
      numVerts,
      indexes: readTriangles(r, s + r.i32(s + 88), r.i32(s + 84)),
      st: readSt(r, s + r.i32(s + 96), numVerts),
      xyz,
      normals,
    });
    s += r.i32(s + 104);
  }
  return { name: r.str(8), numFrames, surfaces, tags };
}

/** R_LerpTag for vertex models: lerped origin and axis, axes renormalized. Null if there is no such tag. */
export function meshTag(model: MeshModel, tagName: string, frame: number, oldFrame: number, backlerp: number): Orientation | null {
  const tag = model.tags.get(tagName.toLowerCase());
  if (!tag) return null;
  const n = model.numFrames;
  const f = Math.min(Math.max(frame, 0), n - 1);
  const of = Math.min(Math.max(oldFrame, 0), n - 1);
  const front = 1 - backlerp;
  const origin: Vec3 = [0, 0, 0];
  for (let k = 0; k < 3; k++) origin[k] = tag.origin[of * 3 + k] * backlerp + tag.origin[f * 3 + k] * front;
  const axis: Axis = [
    [0, 0, 0],
    [0, 0, 0],
    [0, 0, 0],
  ];
  for (let i = 0; i < 3; i++) {
    for (let k = 0; k < 3; k++) axis[i][k] = tag.axis[of * 9 + i * 3 + k] * backlerp + tag.axis[f * 9 + i * 3 + k] * front;
    const len = Math.hypot(axis[i][0], axis[i][1], axis[i][2]) || 1;
    for (let k = 0; k < 3; k++) axis[i][k] /= len;
  }
  return { origin, axis };
}
